import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  createHelpSession,
  fetchHelpNudge,
  sendHelpMessage,
  updateHelpSessionContext,
} from "../api/client";
import { useTheme } from "../context/ThemeContext";
import { buildHelpContext, buildHelpNudgeFallback } from "../utils/helpContext";
import { isFiveXType, productName } from "../utils/participantAssessmentType";

const NUDGE_IDLE_MS = 120000;

export default function AssessmentHelpPanel({
  surveyToken,
  step,
  sectionTitle,
  pillars = [],
  questions = [],
  focusedPillarStep,
  focusedQuestionId,
  lastAnsweredQuestionId,
  firstUnansweredQuestionId,
  assessmentTrack = "full",
  suggestedQuestions = [],
  activityKey = 0,
  allAnswered = false,
  externalQuestionRequest,
  assessmentType,
}) {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [booting, setBooting] = useState(false);
  const [showNudge, setShowNudge] = useState(false);
  const [nudge, setNudge] = useState(null);
  const [dismissKey, setDismissKey] = useState(0);
  const [chips, setChips] = useState([]);
  const [pendingQuestion, setPendingQuestion] = useState(null);
  const listRef = useRef(null);
  const helpContextRef = useRef(null);
  const lastContextSyncReasonRef = useRef("");
  const { theme } = useTheme();
  const dark = theme === "dark";

  const helpContext = useMemo(
    () =>
      buildHelpContext({
        step,
        sectionTitle,
        pillars,
        questions,
        assessmentTrack,
        focusedPillarStep,
        focusedQuestionId,
        lastAnsweredQuestionId,
        firstUnansweredQuestionId,
        productName: productName(assessmentType),
        typeKey: assessmentType?.type_key || assessmentType?.key,
        isFiveX: isFiveXType(assessmentType),
      }),
    [
      step,
      sectionTitle,
      pillars,
      questions,
      assessmentTrack,
      focusedPillarStep,
      focusedQuestionId,
      lastAnsweredQuestionId,
      firstUnansweredQuestionId,
      assessmentType,
    ]
  );

  useEffect(() => {
    helpContextRef.current = helpContext;
  }, [helpContext]);

  useEffect(() => {
    if (!open || sessionId || !surveyToken) return;
    let cancelled = false;
    (async () => {
      setBooting(true);
      try {
        const res = await createHelpSession(surveyToken, helpContext);
        if (cancelled) return;
        setSessionId(res.data.session_id);
        setChips(res.data.suggested_questions || []);
        setMessages([
          {
            role: "assistant",
            content: isFiveXType(assessmentType)
              ? "Assessment help can explain terms and how the 5x Team Assessment works. It cannot suggest how to rate Importance or Agreement."
              : `Assessment help can explain terms and how the ${productName(assessmentType)} works. It cannot suggest how you should answer.`,
          },
        ]);
      } catch (e) {
        if (!cancelled) {
          setMessages([
            {
              role: "assistant",
              content: e.message || "Could not start Assessment help.",
            },
          ]);
        }
      } finally {
        if (!cancelled) setBooting(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, sessionId, surveyToken, helpContext, assessmentType]);

  useEffect(() => {
    if (!sessionId || !surveyToken || !open) return;
    let cancelled = false;
    const reasonKey = `${step ?? ""}|${focusedPillarStep ?? ""}|${focusedQuestionId ?? ""}|${lastAnsweredQuestionId ?? ""}|${firstUnansweredQuestionId ?? ""}|${externalQuestionRequest?.nonce ?? ""}`;
    if (reasonKey === lastContextSyncReasonRef.current) return;
    (async () => {
      try {
        const res = await updateHelpSessionContext(
          surveyToken,
          sessionId,
          helpContextRef.current
        );
        if (cancelled) return;
        lastContextSyncReasonRef.current = reasonKey;
        if (res.data?.suggested_questions?.length) {
          setChips(res.data.suggested_questions);
        }
      } catch {
        /* non-fatal */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    sessionId,
    surveyToken,
    open,
    step,
    focusedPillarStep,
    focusedQuestionId,
    lastAnsweredQuestionId,
    firstUnansweredQuestionId,
    externalQuestionRequest?.nonce,
  ]);

  useEffect(() => {
    listRef.current?.scrollTo(0, listRef.current.scrollHeight);
  }, [messages]);

  useEffect(() => {
    if (open || allAnswered) {
      setShowNudge(false);
      setNudge(null);
      return;
    }
    setShowNudge(false);
    setNudge(null);
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const res = await fetchHelpNudge(surveyToken, helpContextRef.current);
        if (cancelled) return;
        setNudge(res.data);
        setShowNudge(true);
      } catch {
        if (cancelled) return;
        setNudge(
          buildHelpNudgeFallback({
            pillars,
            focusedQuestionId,
            firstUnansweredQuestionId,
            sectionTitle,
          })
        );
        setShowNudge(true);
      }
    }, NUDGE_IDLE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    activityKey,
    step,
    focusedPillarStep,
    focusedQuestionId,
    open,
    dismissKey,
    allAnswered,
    surveyToken,
    sectionTitle,
    pillars,
    firstUnansweredQuestionId,
  ]);

  const send = useCallback(
    async (text) => {
      const trimmed = (text || input).trim();
      if (!trimmed || !sessionId || loading) return;
      setInput("");
      setMessages((m) => [...m, { role: "user", content: trimmed }]);
      setLoading(true);
      try {
        const res = await sendHelpMessage(surveyToken, sessionId, trimmed, helpContext);
        setMessages((m) => [
          ...m,
          { role: "assistant", content: res.data.reply },
        ]);
        if (res.data.suggested_questions?.length) {
          setChips(res.data.suggested_questions);
        }
      } catch (e) {
        setMessages((m) => [
          ...m,
          { role: "assistant", content: e.message || "Something went wrong." },
        ]);
      } finally {
        setLoading(false);
      }
    },
    [input, sessionId, loading, surveyToken, helpContext]
  );

  useEffect(() => {
    if (!pendingQuestion || !sessionId || booting || loading) return;
    const q = pendingQuestion;
    setPendingQuestion(null);
    send(q);
  }, [pendingQuestion, sessionId, booting, loading, send]);

  useEffect(() => {
    if (!externalQuestionRequest?.text) return;
    openPanel(externalQuestionRequest.text);
  }, [externalQuestionRequest?.nonce]);

  const chipItems =
    chips.length > 0
      ? chips
      : suggestedQuestions.length > 0
        ? suggestedQuestions
        : [
            { question: "What is Importance?" },
            { question: "What is Agreement?" },
            { question: "Is my assessment anonymous?" },
          ];

  const hasFollowUpChips = chipItems.some((c) => c.kind === "followup");
  const hasNextChips = chipItems.some((c) => c.kind === "next_question");
  const chipsLabel = hasFollowUpChips
    ? "Follow up"
    : hasNextChips
      ? "Up next"
      : "Suggested";

  const openPanel = (initialQuestion = null) => {
    setShowNudge(false);
    setOpen(true);
    if (initialQuestion) setPendingQuestion(initialQuestion);
  };

  const nudgeMessage =
    nudge?.message ||
    buildHelpNudgeFallback({
      pillars,
      focusedQuestionId,
      firstUnansweredQuestionId,
      sectionTitle,
    }).message;

  const panel = {
    bg: dark ? "#0b1520" : "#ffffff",
    border: dark ? "rgba(255,255,255,0.12)" : "rgba(0,79,115,0.12)",
    title: dark ? "#67e8f9" : "#004f73",
    muted: dark ? "rgba(226,232,240,0.72)" : "#6b7280",
    text: dark ? "#f1f5f9" : "#0f172a",
    bubbleUser: dark ? "rgba(0,79,115,0.45)" : "rgba(0,79,115,0.08)",
    bubbleAi: dark ? "rgba(255,255,255,0.09)" : "#f3f4f6",
    chipBorder: dark ? "rgba(103,232,249,0.4)" : "rgba(0,79,115,0.3)",
    chipText: dark ? "#67e8f9" : "#004f73",
    chipBg: dark ? "rgba(56,189,248,0.12)" : "transparent",
    inputBg: dark ? "rgba(255,255,255,0.06)" : "#fff",
    inputBorder: dark ? "rgba(255,255,255,0.18)" : "#d1d5db",
    placeholder: dark ? "rgba(148,163,184,0.75)" : "#9ca3af",
  };

  return (
    <>
      {showNudge && !open && (
        <div
          role="status"
          aria-live="polite"
          className="fixed bottom-16 right-4 md:bottom-[4.5rem] md:right-6 z-40 shadow-xl rounded-xl px-4 py-3 max-w-[16rem] pointer-events-auto"
          style={{
            animation: "nudgeFadeIn 0.3s ease",
            background: panel.bg,
            border: `1px solid ${panel.border}`,
            color: panel.text,
          }}
        >
          <button
            type="button"
            aria-label="Dismiss"
            className="absolute top-1.5 right-2 text-sm leading-none"
            style={{ color: panel.muted }}
            onClick={() => {
              setShowNudge(false);
              setDismissKey((k) => k + 1);
            }}
          >
            ×
          </button>
          <p className="text-xs font-semibold pr-4" style={{ color: panel.title }}>
            Need help?
          </p>
          <p className="text-xs mt-1 mb-2 leading-relaxed" style={{ color: panel.muted }}>
            {nudgeMessage}
          </p>
        </div>
      )}

      <style>{`
        @keyframes nudgeFadeIn {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .help-chip-scroll {
          scrollbar-width: thin;
          scrollbar-color: ${dark ? "rgba(103,232,249,0.35) transparent" : "rgba(0,79,115,0.25) transparent"};
        }
        .help-chip-scroll::-webkit-scrollbar { height: 4px; }
        .help-chip-scroll::-webkit-scrollbar-thumb {
          background: ${dark ? "rgba(103,232,249,0.35)" : "rgba(0,79,115,0.25)"};
          border-radius: 999px;
        }
        .help-panel-input::placeholder {
          color: ${panel.placeholder};
        }
      `}</style>

      <button
        type="button"
        className="fixed bottom-4 right-4 z-40 shadow-lg md:bottom-6 md:right-6 inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold text-white"
        style={{
          background: "linear-gradient(105deg,#004f73 0%,#1a7fa8 55%,#1a9bb8 100%)",
          boxShadow: "0 10px 28px rgba(0,79,115,0.45)",
        }}
        onClick={() => openPanel()}
        aria-expanded={open}
      >
        <span
          aria-hidden
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: "#67e8f9",
            boxShadow: "0 0 10px #67e8f9",
          }}
        />
        AI help
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-0 md:p-4"
          style={{ background: dark ? "rgba(0,0,0,0.65)" : "rgba(0,0,0,0.4)" }}
          role="dialog"
          aria-label="Assessment help"
        >
          <div
            className="w-full md:max-w-md md:rounded-xl shadow-xl flex flex-col max-h-[85vh] md:max-h-[38rem]"
            style={{
              background: panel.bg,
              border: `1px solid ${panel.border}`,
              color: panel.text,
            }}
          >
            <div
              className="flex items-center justify-between px-4 py-3 shrink-0"
              style={{ borderBottom: `1px solid ${panel.border}` }}
            >
              <div>
                <h2 className="font-semibold" style={{ color: panel.title }}>
                  Assessment help
                </h2>
                {sectionTitle && (
                  <p className="text-xs mt-0.5" style={{ color: panel.muted }}>
                    {sectionTitle}
                  </p>
                )}
              </div>
              <button
                type="button"
                className="text-xl leading-none px-2"
                style={{ color: panel.muted }}
                onClick={() => setOpen(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div
              ref={listRef}
              className="flex-1 overflow-y-auto px-4 py-3 space-y-3 text-sm min-h-0"
            >
              {booting && (
                <p style={{ color: panel.muted }}>Loading help…</p>
              )}
              {messages.map((msg, i) => (
                <div
                  key={i}
                  className={`rounded-lg px-3 py-2 whitespace-pre-wrap break-words ${
                    msg.role === "user" ? "ml-8" : "mr-4"
                  }`}
                  style={{
                    background:
                      msg.role === "user" ? panel.bubbleUser : panel.bubbleAi,
                    color: panel.text,
                  }}
                >
                  {msg.content}
                </div>
              ))}
              {loading && (
                <p className="italic" style={{ color: panel.muted }}>
                  Thinking…
                </p>
              )}
            </div>
            <div
              className="px-4 py-2 shrink-0"
              style={{ borderTop: `1px solid ${panel.border}` }}
            >
              <p
                className="text-[10px] font-semibold uppercase tracking-wide mb-1.5"
                style={{ color: panel.muted }}
              >
                {chipsLabel}
              </p>
              <div className="flex gap-2 overflow-x-auto help-chip-scroll">
                {chipItems.map((c, i) => (
                  <button
                    key={`${c.kind || "chip"}-${i}`}
                    type="button"
                    className="text-xs rounded-full px-2 py-1 whitespace-nowrap shrink-0"
                    style={{
                      border: `1px solid ${panel.chipBorder}`,
                      color: panel.chipText,
                      background: panel.chipBg,
                    }}
                    onClick={() => send(c.question)}
                    disabled={loading || !sessionId}
                  >
                    {c.label ||
                      (c.kind === "next_question" &&
                      c.question.startsWith("Explain question ")
                        ? `explain:${
                            c.question_id ||
                            c.question.match(/Explain question ([^\s:]+)/i)?.[1] ||
                            ""
                          }`
                        : c.question)}
                  </button>
                ))}
              </div>
            </div>
            <form
              className="p-3 flex gap-2"
              style={{ borderTop: `1px solid ${panel.border}` }}
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
            >
              <input
                type="text"
                className="flex-1 rounded-lg px-3 py-2 text-sm help-panel-input"
                style={{
                  background: panel.inputBg,
                  border: `1px solid ${panel.inputBorder}`,
                  color: panel.text,
                }}
                placeholder="Ask about a term or the assessment…"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                disabled={loading || !sessionId}
              />
              <button
                type="submit"
                className="text-sm px-4 rounded-lg font-semibold text-white disabled:opacity-50"
                style={{
                  background: "linear-gradient(105deg,#004f73,#1a9bb8)",
                }}
                disabled={loading || !sessionId}
              >
                Send
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
